// src/pages/dashboards/admin/AdminVouchers.jsx
// Voucher list with create + activate/deactivate toggle.

import React, { useEffect, useState } from "react";
import { listVouchers, createVoucher, setVoucherActive } from "../../../api/admin";
import { Card, Input, Select, Button, Badge, Loading, EmptyState, Modal, Field } from "../../../components/ui";
import { Plus } from "../../../components/icons";
import { date, titleCase } from "../../../lib/format";
import { useToast } from "../../../context/ToastContext";

const TYPES = ["percentage", "fixed"];

function CreateVoucherModal({ onClose, onCreated }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ code: "", discount_type: "percentage", discount_value: "", max_uses: "", expires_at: "" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: f.code.trim(),
        discount_type: f.discount_type,
        discount_value: parseFloat(f.discount_value),
      };
      if (f.max_uses) payload.max_uses = parseInt(f.max_uses, 10);
      if (f.expires_at) payload.expires_at = f.expires_at;
      await createVoucher(payload);
      toast.success("Voucher created");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="New voucher">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Code"><Input value={f.code} onChange={set("code")} required placeholder="LAUNCH50" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Type">
            <Select value={f.discount_type} onChange={set("discount_type")}>
              {TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
            </Select>
          </Field>
          <Field label="Value" hint={f.discount_type === "percentage" ? "percent off" : "ZAR off"}>
            <Input type="number" step="0.01" min="0" value={f.discount_value} onChange={set("discount_value")} required />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Max uses" hint="blank = unlimited"><Input type="number" min="1" value={f.max_uses} onChange={set("max_uses")} /></Field>
          <Field label="Expires" hint="optional"><Input type="date" value={f.expires_at} onChange={set("expires_at")} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Creating..." : "Create voucher"}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default function AdminVouchers() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => listVouchers().then(setRows).catch((e) => { toast.error(e.message); setRows([]); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = async (v) => {
    try {
      await setVoucherActive(v.id, !v.is_active);
      toast.success(v.is_active ? "Deactivated" : "Activated");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const valueLabel = (v) =>
    v.discount_type === "percentage" ? `${v.discount_value}%` : `R${v.discount_value}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-display font-bold">Vouchers</h1>
        <Button onClick={() => setCreating(true)}><Plus size={16} /> New voucher</Button>
      </div>

      <Card>
        {rows === null ? (
          <Loading label="Loading vouchers" />
        ) : rows.length === 0 ? (
          <EmptyState title="No vouchers yet" hint="Create a code to get started." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-tb-muted border-b border-tb-border dark:border-tb-d-border">
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Type</th>
                  <th className="px-4 py-3 font-medium">Value</th>
                  <th className="px-4 py-3 font-medium">Uses</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Expires</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className="border-b border-tb-border/60 dark:border-tb-d-border/60 last:border-0">
                    <td className="px-4 py-3 font-mono font-medium">{v.code}</td>
                    <td className="px-4 py-3">{titleCase(v.discount_type)}</td>
                    <td className="px-4 py-3">{valueLabel(v)}</td>
                    <td className="px-4 py-3">{v.used_count}{v.max_uses ? ` / ${v.max_uses}` : ""}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-tb-muted">{v.expires_at ? date(v.expires_at) : "—"}</td>
                    <td className="px-4 py-3"><Badge tone={v.is_active ? "green" : "gray"}>{v.is_active ? "Active" : "Inactive"}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" onClick={() => toggle(v)}>{v.is_active ? "Deactivate" : "Activate"}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <CreateVoucherModal onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}